import { useEffect, useMemo, useState } from "react";
import { useApiContext } from 'context/ApiContext';

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDDatePicker from "components/MDDatePicker";
import MDMapLeaflet from "components/MDMapLeaflet";
import MDTypography from "components/MDTypography";
import MDMapLibre from "components/MDMapLibre";

// Material Dashboard 2 React example components
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DataTable from "examples/Tables/DataTableV2";
import TurbineDashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TurbineDashboardNavbar from "examples/Navbars/DashboardNavbar";

function ParkDashboard() {
    const { alertsNumPaD, APIRequestType, fromDatePaD, getAPI, parkPaD, setAlertsNumPaD, setFromDatePaD, setParkPaD, setToDatePaD, setTopActivityPaD,
        setTurbinesPaD, toDatePaD, topActivityPaD, turbinesPaD } = useApiContext();
    const [parkIDPaD, setParkIDPaD] = useState(null);  // Utiliser un etat local pour stocker l'ID du parc

    // Charger l'ID du parc depuis localStorage
    useEffect(() => {
        const savedParkID = localStorage.getItem('selectedParkID');
        if (!savedParkID) return;
        console.log(" 0 0 1   PaD");
        setParkIDPaD(savedParkID);  // Mettre a jour l'etat avec l'ID du parc recupere
    }, []);  // L'effet se declenche uniquement au chargement initial du composant


    useEffect(() => {
        if (!parkIDPaD) return;  // evite un appel API inutile si l'ID du parc n'est pas encore charge
        console.log(" 0 0 2   PaD");
        getAPI(APIRequestType.GET_PARK);
        getAPI(APIRequestType.GET_PARK_TURBINES);
    }, [parkIDPaD]);

    useEffect(() => {
        if (!parkIDPaD) return;  // evite un appel API inutile si l'ID du parc n'est pas encore charge
        console.log(" 0 0 3   PaD");
        getAPI(APIRequestType.GET_PARK_TURBINES);
    }, [fromDatePaD, toDatePaD]);

    useEffect(() => {
        return () => {
            resetPaD();
        };
    }, []);

    const resetPaD = () => {
        setParkPaD(null);
        setTurbinesPaD([]);
        setAlertsNumPaD(0);
        setTopActivityPaD('');
    };

    // Tableau de points GPS a afficher sur la carte
    const points = useMemo(() =>
        turbinesPaD.map(turbine => {
            const [lat, lng] = turbine.position.split(',').map(coord => parseFloat(coord.trim()));
            return {
                lat,
                lng,
                id: turbine.id,
                popupText: `E${turbine.number}\n${turbine.position}`,
            };
        }), [turbinesPaD]);

    const handleFromDateChange = (date) => {
        setFromDatePaD(date || null);
        if (date && toDatePaD && date > toDatePaD) {
            setToDatePaD(date);
        }
    };

    const handleToDateChange = (date) => {
        setToDatePaD(date || null);
    };

    return (
        <TurbineDashboardLayout>
            <TurbineDashboardNavbar />
            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={3} mt={0} mb={1} ml={-0.5}>
                <MDBox display="flex" alignItems="center" gap={0}>
                    {/* Date Picker "From" */}
                    <MDDatePicker
                        label="From"
                        value={fromDatePaD}
                        onChange={handleFromDateChange}
                        scale={0.9}
                    />

                    {/* Date Picker "To" */}
                    <MDDatePicker
                        label="To"
                        value={toDatePaD}
                        onChange={handleToDateChange}
                        minDate={fromDatePaD} // Empeche de choisir une date anterieure
                        scale={0.9}
                    />
                </MDBox>

                <MDBox display="flex" flexDirection="column" alignItems="flex-end" mr={2.5} mb={2}>
                    {parkPaD && parkPaD.name && (
                        <MDTypography variant="button"
                            fontWeight="bold"
                            color="dark"
                            ml={1}
                            fontSize="1.2rem">
                            {parkPaD.name}
                        </MDTypography>
                    )}
                    {parkPaD && parkPaD.address && (
                        <MDTypography variant="button"
                            fontWeight="light"
                            color="dark"
                            ml={1}
                            fontSize="0.8rem">
                            {parkPaD.address}
                        </MDTypography>
                    )}
                </MDBox>
            </MDBox>

            <MDBox py={2}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6} lg={3}>
                        <MDBox mb={1.5}>
                            {alertsNumPaD > 1 && (
                                <ComplexStatisticsCard
                                    color="info"
                                    icon="crisis_alert"
                                    title="Detections"
                                    count={alertsNumPaD}
                                    lines={1}
                                />
                            )}
                            {alertsNumPaD < 2 && (
                                <ComplexStatisticsCard
                                    color="info"
                                    icon="crisis_alert"
                                    title="Detection"
                                    count={alertsNumPaD}
                                    lines={1}
                                />
                            )}
                        </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <MDBox mb={1.5}>
                            <ComplexStatisticsCard
                                color="info"
                                icon="agriculture"
                                title="Top Activity"
                                count={topActivityPaD}
                                lines={2}
                            />
                        </MDBox>
                    </Grid>
                </Grid>
            </MDBox>

            <MDBox display="flex" alignItems="center" justifyContent="center" >
                {turbinesPaD.length == 0 && (
                    <MDTypography variant="button"
                        fontWeight="bold"
                        color="secondary"
                        opacity={0.4}
                        ml={1}
                        fontSize="5rem">
                        No Turbine
                    </MDTypography>
                )}
            </MDBox>

            <MDBox pb={3}>
                {turbinesPaD.length > 0 && (
                    <MDMapLibre mapType="parkDashboard" points={points} />
                )}
            </MDBox>

            <MDBox pt={3} pb={3}>
                {turbinesPaD.length > 0 && (
                    <Grid container spacing={6}>
                        <Grid item xs={12}>
                            <Card>
                                <MDBox
                                    mx={2}
                                    mt={-3}
                                    py={1.5}
                                    px={2}
                                    variant="gradient"
                                    bgColor="info"
                                    borderRadius="lg"
                                    coloredShadow="info"
                                >
                                    <MDTypography variant="h6" color="white">
                                        Turbine List
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" justifyContent="flex-end" alignItems="flex-end" mt={1.5} mr={2.5} mb={-2.5}>
                                    <MDTypography variant="button"
                                        fontWeight="light"
                                        color="dark"
                                        ml={1}
                                        fontSize="1rem">
                                        Turbine Number :
                                    </MDTypography>
                                    <MDTypography variant="button"
                                        fontWeight="bold"
                                        color="dark"
                                        ml={1}
                                        fontSize="1rem">
                                        {turbinesPaD.length}
                                    </MDTypography>
                                </MDBox>
                                <MDBox pt={3}>
                                    <DataTable
                                        table={{
                                            columns: [
                                                { Header: "Turbine", accessor: "turbine", align: "left" },
                                                { Header: "Detection(s)", accessor: "detection", align: "center" },
                                                { Header: "Top Activity", accessor: "topActivity", align: "center" },
                                                { Header: "Bridage Active", accessor: "bridageActive", align: "center" },
                                            ],
                                            rows: turbinesPaD.map((turbine) => ({
                                                turbineValue: turbine.number,
                                                turbineidValue: turbine.id,
                                                detectionValue: turbine.alertCount,
                                                topActivityValue: turbine.mostUsedActivity?.type || '__',
                                                bridageActiveValue: turbine.bridage,

                                                turbine: (
                                                    <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                        E{turbine.number}
                                                    </MDTypography>
                                                ),
                                                detection: (
                                                    <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                        {turbine.alertCount}
                                                    </MDTypography>
                                                ),
                                                topActivity: (
                                                    <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                        {turbine.mostUsedActivity?.type || '__'}
                                                    </MDTypography>
                                                ),
                                                bridageActive: (
                                                    <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                        {turbine.bridage ? "YES" : "NO"}
                                                    </MDTypography>
                                                ),
                                            })),
                                        }}
                                        isSorted={false}
                                        entriesPerPage={false}
                                        showTotalEntries={false}
                                        noEndBorder
                                    />
                                </MDBox>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </MDBox>
        </TurbineDashboardLayout>
    );
}

export default ParkDashboard;
